import { CheckboxGroupInput, Modal } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { usePredefinedToasts } from 'utils/Toast';
import { ModalProps } from 'store/modal';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Stack } from '@mui/material';
import {
  NotificationMethod,
  notificationMethods,
  useResendNotifications,
  useUpdateSurvey,
} from 'store/surveys';

interface Values {
  notificationMethods: NotificationMethod[];
}

interface Props extends ModalProps {
  surveyId: string;
}

export const SurveyWebAccessModal: FC<Props> = ({ surveyId, closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: {
      notificationMethods: ['email', 'appNotification'],
    },
  });

  const { mutate: updateSurvey, isLoading } = useUpdateSurvey();
  const { mutate: resendNotifications, isLoading: isResendingNotifications } =
    useResendNotifications();

  const onSubmit: SubmitHandler<Values> = async (values) => {
    updateSurvey(
      {
        surveyId,
        data: {
          hasWebAccess: true,
          notificationMethods: values.notificationMethods,
        },
      },
      {
        onSuccess: () => {
          resendNotifications(
            { surveyId, data: { methods: values.notificationMethods } },
            {
              onSuccess: () => {
                closeModal();
                predefinedToasts.success.updated();
              },
            },
          );
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('surveys.actions.allow_web_access')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: isLoading || isResendingNotifications,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="notificationMethods"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <CheckboxGroupInput
                  label={t('surveys.setNotifications')}
                  items={notificationMethods.map((method) => ({
                    label: t(`news.send_method.${method}`),
                    value: method,
                  }))}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
